import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'
import { reaction, observable } from 'mobx'
import get from 'lodash/get'
import isNaN from 'lodash/isNaN'
import { NULL as BlockModelNULL } from '@client/components/Block/model'

import TimerModel, { NULL as TimerModelNULL } from './model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import { SECTION_TYPES } from '@client/components/Section/constants'
import findAncestor from '@client/utils/findAncestor'
import reservedImageHeight from '@client/utils/reservedImageHeight'
import { getPaddingTotal } from '../../Block/component'

const getPaddingReduction = ({ kind, section, columnIndex, fullWidth }) => {
  if (fullWidth) {
    return 0
  }
  const sectionType = get(section, 'sectionType')
  if (
    sectionType === SECTION_TYPES.LANDINGONECOL ||
    sectionType === SECTION_TYPES.LANDINGTWOCOL
  ) {
    return 0
  }
  return getPaddingTotal({ kind, section, columnIndex, fullWidth })
}

export class Banner extends Component {
  @observable editingSettings = BlockModelNULL

  static propTypes = {
    model: PropTypes.instanceOf(TimerModel),
    editor: PropTypes.instanceOf(EditorModel),
    app: PropTypes.instanceOf(AppModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    model: TimerModelNULL,
    editor: EditorModelNULL,
    app: AppModelNULL,
    serverRender: false,
  }

  disposeReaction = null

  constructor(props) {
    super(props)

    this.el = React.createRef()

    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL

    this.disposeReaction = reaction(
      () => this.editingSettings,
      (values) => {
        this.setContainerDimentions()
      }
    )

    // Toggling fullWidth changes the padding reduction in setContainerDimentions
    // but does NOT physically resize the observed tableCell, so ResizeObserver
    // won't fire. Use a mobx reaction (not componentDidUpdate prevProps) — under
    // @observer, prevProps.model and this.props.model reference the same
    // instance, so prevProps.model.settings.values.fullWidth resolves to the
    // new value too and the comparison would always be equal.
    const model = props.model
    this.disposeFullWidthReaction = reaction(
      () => model.settings.values.fullWidth,
      () => {
        this.setContainerDimentions()
      }
    )
  }

  componentDidMount() {
    this.tableCell = this.getTableCell()
    this.setContainerDimentions()
    if (this.tableCell) {
      this.resizeObserver = new ResizeObserver(() => this.setContainerDimentions())
      this.resizeObserver.observe(this.tableCell)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.disposeReaction) {
      this.disposeReaction()
    }
    if (this.disposeFullWidthReaction) {
      this.disposeFullWidthReaction()
    }
  }

  getTableCell = () => {
    return findAncestor(this.el.current, 'gan-block-parent')
  }

  setContainerDimentions() {
    const section = get(this.props, 'model.section')
    const columnIndex = this.props.model.columnIndex
    const kind = this.props.model.kind
    const fullWidth = this.props.model.settings.values.fullWidth

    const padding = getPaddingReduction({ kind, section, columnIndex, fullWidth })

    const tableCell = this.tableCell
    const clientWidth = get(tableCell, 'clientWidth')
    this.props.model.setContainerDimensions({
      width: clientWidth - padding,
    })
  }

  render() {
    const { model, serverRender } = this.props
    const globalSettings = this.props.editor.settings.values
    const settings = model.settings.values
    const width = settings.width

    const [_, sizes] = settings.image.split('-')
    const [imageWidth, imageHeight] = sizes.split('x')

    const imageUrl = `https://ads.getanewsletter.com/b/a${settings.userId}/${settings.image}.png`
    const linkUrl = `${imageUrl}/click/`
    const altText = 'Get a Newsletter – Enkelt verktyg för nyhetsbrev'

    const fontSize = settings.fontSize || globalSettings.normalText_fontSize

    const className = `${serverRender ? '' : 'banner'}`

    const widthCheckCondition = !!width && typeof width !== 'undefined' && !isNaN(width) && width !== 'NaN'

    const computedWidth = width > imageWidth ? imageWidth : width

    const imgStyle = {
      minHeight: serverRender ? undefined : '20px',
      display: 'block',
      border: 0,
      height: 'auto',
      margin: 0,
      width: widthCheckCondition ? `${computedWidth}px` : undefined,
      maxWidth: widthCheckCondition ? `100%` : undefined,
      fontSize: fontSize + 'px',
    }

    return (
      <a
        notrack="notrack"
        href={linkUrl}
        style={{ display: 'block', textDecoration: 'none' }}
      >
        <img
          className={className}
          ref={this.el}
          src={imageUrl}
          alt={altText}
          title={altText}
          width={widthCheckCondition ? computedWidth : undefined}
          // Reserve the box from the ad's known dimensions so a blocked banner
          // keeps its height and doesn't shift a two-column layout (FE-1128).
          height={
            widthCheckCondition
              ? reservedImageHeight(computedWidth, imageWidth, imageHeight)
              : undefined
          }
          style={imgStyle}
        />
      </a>
    )
  }
}

export default inject('app', 'editor', 'serverRender')(observer(Banner))
